import type { TagDraft } from 'redux/modules/tags.types';
import type { Tag } from 'types/tag';

export type TagStatusFilter = 'active' | 'archived' | 'all';
export type TagBulkOperation = 'archive' | 'restore' | 'delete' | 'move';
export const matchesTagStatus = (tag: Tag | TagDraft, status: TagStatusFilter) =>
  status === 'all' || (status === 'active' ? tag.active !== false : tag.active === false);

// Retain a list as context when one of its children matches the view.
export function filterTagTree(
  tags: (Tag | TagDraft)[],
  status: TagStatusFilter,
  search: string
): (Tag | TagDraft)[] {
  const query = search.trim().toLowerCase();
  return tags.flatMap(tag => {
    if (!tag.id) return [tag];
    const nameMatches = tag.name?.toLowerCase().includes(query) ?? false;
    const children = (('children' in tag && tag.children) || []).filter(
      child =>
        !child.id ||
        (matchesTagStatus(child, status) &&
          (nameMatches || child.name.toLowerCase().includes(query)))
    );
    return (matchesTagStatus(tag, status) && nameMatches) || children.length
      ? [{ ...tag, children }]
      : [];
  });
}

export function visibleTagIds(tags: Tag[], status: TagStatusFilter, search: string): number[] {
  const query = search.trim().toLowerCase();
  return tags.flatMap(tag => {
    const nameMatches = tag.name.toLowerCase().includes(query);
    return [
      ...(nameMatches && matchesTagStatus(tag, status) ? [tag.id] : []),
      ...(tag.children ?? [])
        .filter(
          child =>
            matchesTagStatus(child, status) &&
            (nameMatches || child.name.toLowerCase().includes(query))
        )
        .map(child => child.id),
    ];
  });
}

export function affectedTagIds(
  tags: Tag[],
  selectedIds: number[],
  operation: TagBulkOperation
): number[] {
  const selected = new Set(selectedIds);
  return [
    ...new Set(
      tags.flatMap(tag => {
        const children = tag.children ?? [];
        const affected =
          selected.has(tag.id) && ['archive', 'delete'].includes(operation)
            ? [tag, ...children]
            : [tag, ...children].filter(tag => selected.has(tag.id));
        return affected.filter(tag => operation !== 'archive' || tag.active).map(tag => tag.id);
      })
    ),
  ];
}

// A selected list always includes its whole subtree. Dropping any child must
// drop the list too, otherwise archive/delete would still cascade to that child.
export function normalizeTagSelection(tags: Tag[], ids: number[]): number[] {
  const selected = new Set(ids);
  for (const list of tags) {
    if (list.children?.some(child => !selected.has(child.id))) selected.delete(list.id);
  }
  return [...selected];
}

export function toggleTagSelection(tags: Tag[], ids: number[], id: number): number[] {
  const selected = new Set(ids);
  const list = tags.find(tag => tag.id === id);
  const group = [id, ...(list?.children ?? []).map(child => child.id)];
  const deselect = selected.has(id);
  for (const item of group) {
    if (deselect) selected.delete(item);
    else selected.add(item);
  }
  return normalizeTagSelection(tags, [...selected]);
}

// Select all is view-scoped: omit a list when some of its children are hidden,
// so a later archive/delete cannot implicitly include unselected hidden tags.
export function visibleSelectionIds(tags: Tag[], visibleIds: number[]): number[] {
  const visible = new Set(visibleIds);
  const partialLists = new Set(
    tags.filter(tag => tag.children?.some(child => !visible.has(child.id))).map(tag => tag.id)
  );
  return visibleIds.filter(id => !partialLists.has(id));
}

export function setVisibleTagSelection(
  tags: Tag[],
  ids: number[],
  visibleIds: number[],
  checked: boolean
): number[] {
  const selected = new Set(ids);
  for (const id of visibleIds) {
    if (checked) selected.add(id);
    else selected.delete(id);
  }
  return normalizeTagSelection(tags, [...selected]);
}
